import { useEffect, useRef, useState } from "react";
import { JitsiMeeting } from "@jitsi/react-sdk";
import { X, Loader2 } from "lucide-react";
import Portal from "../common/Portal";
import api from "../../api";
import { leaveJitsi, hangupNow } from "../../utils/jitsiLeave";
import { zegoCallConfig } from "../../utils/zegoCallConfig";

// Voice call between a clinician and their supervisor (audio only for now:
// cameras stay off and there's no camera button, which keeps the load on our
// video bridge small) (backend POST
// /supervision/<id>/join). The provider comes from the "Supervision" order in
// Admin -> Video settings. No patient is on the call, so there's no
// transcription, consent or attendance step. No screen sharing, as for
// patient calls: a clinician's screen holds other patients' details.
export default function SupervisionCallModal({ request, onClose }) {
  const [state, setState] = useState({ status: "loading" });
  // Hang up in Jitsi before closing, so a rejoin isn't blocked by our own
  // leftover seat (rooms hold two; see utils/jitsiLeave.js).
  const jitsiRef = useRef(null);
  const leave = async () => {
    const jitsi = jitsiRef.current;
    jitsiRef.current = null;
    await leaveJitsi(jitsi);
    onClose();
  };
  useEffect(() => {
    const onPageHide = () => hangupNow(jitsiRef.current);
    window.addEventListener("pagehide", onPageHide);
    return () => {
      window.removeEventListener("pagehide", onPageHide);
      if (jitsiRef.current) leaveJitsi(jitsiRef.current, 0);
      jitsiRef.current = null;
    };
  }, []);
  useEffect(() => {
    let cancelled = false;
    api.joinSupervision(request.id)
      .then((r) => { if (!cancelled) setState({ status: "ready", provider: r.data.provider, creds: r.data.credentials }); })
      .catch((e) => { if (!cancelled) setState({ status: "error", error: e?.response?.data?.error || "Couldn't start the call." }); });
    return () => { cancelled = true; };
  }, [request.id]);

  useEffect(() => {
    if (state.provider !== "zego" || !state.creds) return undefined;
    let zp;
    let guards;
    import("@zegocloud/zego-uikit-prebuilt").then(({ ZegoUIKitPrebuilt }) => {
      const container = document.getElementById("supervision-zego-container");
      if (!container) return;
      const c = state.creds;
      zp = ZegoUIKitPrebuilt.create(ZegoUIKitPrebuilt.generateKitTokenForProduction(c.app_id, c.token, c.room_name, c.user_id, c.user_name));
      // Voice only for now (no camera button); same guards as patient calls.
      guards = zegoCallConfig({ ZegoUIKitPrebuilt, getZp: () => zp, audioOnly: true, durationMinutes: 90, onLeaveRoom: onClose });
      zp.joinRoom({ container, ...guards.config });
    });
    return () => { guards?.dispose(); try { zp?.destroy(); } catch (e) { /* best-effort cleanup */ } };
  }, [state.provider, state.creds]);

  const other = request.is_supervisor ? request.clinician_name : request.supervisor_name;
  return (
    <Portal>
      <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 sm:p-4">
        <div className="flex h-full w-full flex-col overflow-hidden bg-white sm:h-[85vh] sm:max-w-5xl sm:rounded-2xl">
          <div className="flex items-center justify-between border-b border-secondary-100 px-4 py-2.5">
            <div>
              <p className="text-sm font-bold text-secondary-900">Supervision with {other}</p>
              <p className="text-xs text-secondary-500">About {request.patient_name} · voice only · not transcribed</p>
            </div>
            <button type="button" onClick={leave} aria-label="Leave call" className="rounded-lg p-2 text-secondary-600 hover:bg-secondary-100"><X className="w-5 h-5" /></button>
          </div>
          <div className="relative flex-1 min-h-0 overflow-hidden bg-secondary-900">
            {state.status === "loading" && <div className="flex h-full items-center justify-center text-sm text-white"><Loader2 className="mr-2 w-4 h-4 animate-spin" /> Connecting…</div>}
            {state.status === "error" && <div className="flex h-full items-center justify-center px-6 text-center text-sm text-white">{state.error}</div>}
            {state.status === "ready" && state.provider === "jitsi" && (
              <JitsiMeeting
                domain={state.creds.domain} roomName={state.creds.room_name} jwt={state.creds.jwt}
                configOverwrite={{ disableDeepLinking: true, deeplinking: { disabled: true }, p2p: { enabled: false }, // always via the bridge, see VideoCallModal
                  startWithVideoMuted: true, startAudioOnly: true,
                  toolbarButtons: ["microphone", "hangup", "chat", "raisehand", "settings", "fullscreen"] }}
                getIFrameRef={(el) => { el.style.height = "100%"; el.style.width = "100%"; }}
                onApiReady={(externalApi) => { jitsiRef.current = externalApi; externalApi.on("readyToClose", () => { jitsiRef.current = null; onClose(); }); }}
              />
            )}
            {state.status === "ready" && state.provider === "zego" && <div id="supervision-zego-container" className="absolute inset-0 overflow-hidden" />}
            {state.status === "ready" && state.provider === "google_meet" && (
              <div className="flex h-full flex-col items-center justify-center gap-3 bg-white px-6 text-center">
                <p className="text-sm text-secondary-600">This call opens in Google Meet.</p>
                <a href={state.creds.meeting_link} target="_blank" rel="noopener noreferrer" className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white">Open Google Meet</a>
              </div>
            )}
          </div>
        </div>
      </div>
    </Portal>
  );
}
